import { Dialog, DialogContent } from "@mui/material";
import React, { useState } from "react";

import "drawflow/dist/drawflow.min.css";
import GridBackground from "@/assets/backgrouds/grid.png";
import MDBox from "@/components/atoms/MDBox";
import WorkflowStepForm from "@/components/screens/WorkflowFormPage/components/WorkflowStepsTab/components/WorkflowStepForm";
import { normalizeWorkflowStepFormData } from "@/components/screens/WorkflowFormPage/components/WorkflowStepsTab/form-data";
import SWR_KEYS from "@/constants/swr-keys";
import useUpdateWorkflowStep from "@/hooks/requests/workflowStep/use-update-workflow-step";
import useDialog from "@/hooks/use-dialog";
import useDrawflowInitializer from "@/hooks/use-drawflow-initializer";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import { IWorkflowStep, IWorkflowStepInput } from "@/interfaces/workflow-step.interface";
import { IWorkflow } from "@/interfaces/workflow.interface";
import { WorkflowMessages } from "@/messages/workflow/workflow.messages";

import ActionButtonsComponent from "./actionsButton";
import { WorkflowNode } from "./workflowNode";

type DrawflowEditorProps = {
  workflow: IWorkflow | null;
  messages: WorkflowMessages;
};

export default function DrawflowEditor({ workflow, messages }: DrawflowEditorProps) {
  const { isVisible, handleOpen, handleClose } = useDialog();
  const { startLoading, endLoading } = useLoading();
  const { mutate } = useMutate();
  const { update, isLoading, loadingIcon } = useUpdateWorkflowStep();
  const [workflowStep, setWorkflowStep] = useState<IWorkflowStep | null>(null);
  const { drawflowRef, editorRef, isInitialized } = useDrawflowInitializer({ workflow });

  const { stepUpdateSuccess, stepUpdateError } = messages;

  const handleUpdate = async (formDataInput: IWorkflowStepInput) => {
    if (!workflowStep || !workflow) return false;
    startLoading();
    const formData = normalizeWorkflowStepFormData(formDataInput, workflow.id);

    try {
      await update({
        _id: workflowStep.id,
        formData,
        successMessage: stepUpdateSuccess,
        errorMessage: stepUpdateError,
      });

      mutate(SWR_KEYS.getWorkflowById);
      mutate(SWR_KEYS.getWorkflowSteps);
      return true;
    } catch (error) {
      return false;
    } finally {
      endLoading();
    }
  };

  const handleSubmitForm = async (formData: IWorkflowStepInput) => {
    const response = await handleUpdate(formData);
    if (response) handleClose();
  };

  return (
    <MDBox p={2}>
      <MDBox
        id="drawflow"
        ref={drawflowRef}
        height="80vh"
        position="relative"
        overflow="hidden"
        sx={{ background: `url(${GridBackground})` }}
      >
        <ActionButtonsComponent
          editorRef={editorRef}
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[900],
            position: "absolute",
            right: 0,
            bottom: 0,
            borderRadius: 2,
            zIndex: 2,
          })}
        />

        {workflow &&
          isInitialized &&
          workflow.workflowSteps.map((step, index) => (
            <WorkflowNode
              key={`${index}-${step.id}-${Date.now()}`}
              step={step}
              editor={editorRef.current}
              handleOpen={handleOpen}
              setWorkflowStep={setWorkflowStep}
              workflow={workflow}
            />
          ))}

        <Dialog
          open={isVisible && !!workflowStep}
          onClose={handleClose}
          style={{ maxHeight: 600, margin: "auto" }}
          fullWidth
          maxWidth="sm"
        >
          <DialogContent>
            {workflow && (
              <WorkflowStepForm
                messages={messages}
                submitForm={handleSubmitForm}
                isLoading={isLoading}
                loadingIcon={loadingIcon}
                step={workflowStep}
              />
            )}
          </DialogContent>
        </Dialog>
      </MDBox>
    </MDBox>
  );
}
